import { Pipe, PipeTransform } from '@angular/core';

/**
 * Determines whether an amount has non-zero cents (e.g. 16.4 -> true, 250 -> false).
 */
function hasNonZeroCents(amount: number): boolean {
  const cents = Math.round(Math.abs(amount) * 100) % 100;
  return cents !== 0;
}

function getFormatter(showTwoDecimals: boolean): Intl.NumberFormat {
  return new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: showTwoDecimals ? 2 : 0,
    maximumFractionDigits: showTwoDecimals ? 2 : 0,
  });
}

/**
 * Formats a single dollar amount per the ALA currency formatting standard:
 * - Always prefixed with `$`
 * - Comma thousands separators for values >= $1,000
 * - Two decimal places only when cents are non-zero (never `.00`, never one digit)
 */
export function formatCurrencyAmount(
  amount: number | null | undefined,
  options?: { forceTwoDecimals?: boolean }
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return '';
  }
  const showTwoDecimals = options?.forceTwoDecimals ?? hasNonZeroCents(amount);
  return `$${getFormatter(showTwoDecimals).format(amount)}`;
}

/**
 * Formats a dollar amount range per the ALA currency formatting standard:
 * - Both ends use the same precision (two decimals if either end has non-zero cents)
 * - Joined with an en dash surrounded by spaces, e.g. "$1,180 – $2,270"
 */
export function formatCurrencyRange(
  min: number | null | undefined,
  max: number | null | undefined
): string {
  if (
    min === null ||
    min === undefined ||
    Number.isNaN(min) ||
    max === null ||
    max === undefined ||
    Number.isNaN(max)
  ) {
    return '';
  }
  const forceTwoDecimals = hasNonZeroCents(min) || hasNonZeroCents(max);
  return `${formatCurrencyAmount(min, {
    forceTwoDecimals,
  })} \u2013 ${formatCurrencyAmount(max, { forceTwoDecimals })}`;
}

/**
 * Reusable currency formatting pipe implementing the ALA currency formatting standard.
 *
 * Usage:
 * - `{{ amount | alaCurrency }}` -> formats a single dollar amount (e.g. "$1,000", "$16.40")
 * - `{{ [minAmount, maxAmount] | alaCurrency }}` -> formats a range (e.g. "$1,180 – $2,270")
 */
@Pipe({
  name: 'alaCurrency',
  standalone: true,
})
export class CurrencyFormatPipe implements PipeTransform {
  transform(value: number | null | undefined): string;
  transform(
    value: [number | null | undefined, number | null | undefined]
  ): string;
  transform(
    value:
      | number
      | [number | null | undefined, number | null | undefined]
      | null
      | undefined
  ): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      const [min, max] = value;
      return formatCurrencyRange(min, max);
    }
    return formatCurrencyAmount(value);
  }
}
